/* Public shareable sizzle-reel landing.  A single big video card + call to
   action back into the vetting funnel.  Zero cinematic 3D chrome — this
   page IS the video.  Same footer/nav as everywhere else so it stays inside
   the DT6 brand system.

   Sharing uses `/api/share/sizzle` (crawler-optimised OG+Twitter markup with
   meta-refresh to /sizzle for humans) so LinkedIn/X/Facebook render an
   inline playable card instead of the plain site OG. */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Share2 } from "lucide-react";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import Section from "@/components/Section";
import SizzlePlayer from "@/components/SizzlePlayer";
import StandardSeal from "@/components/StandardSeal";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export default function SizzlePlayerPage() {
  const shareUrl = `${API_BASE}/api/share/sizzle`;

  // Update the tab title so native share sheets on iOS/Android show
  // reasonable copy (crawler OG is served from /api/share/sizzle).
  useEffect(() => {
    const prev = document.title;
    document.title = "DT6 — America First. America Only.";
    return () => { document.title = prev; };
  }, []);

  const share = () => {
    try {
      if (navigator.share) {
        navigator.share({
          title: "DT6 — America First. America Only.",
          text: "Sixty seconds of the DT6 arsenal.",
          url: shareUrl,
        });
      } else if (navigator.clipboard) {
        navigator.clipboard.writeText(shareUrl);
        // eslint-disable-next-line no-alert
        alert("Share link copied.");
      }
    } catch (_e) { /* no-op */ }
  };
  return (
    <>
      <PublicNav />
      <Section className="pt-20 pb-8" testId="sizzle-hero">
        <div className="flex items-start gap-6 mb-6">
          <StandardSeal size={72} />
          <div>
            <div className="dt6-chip dt6-chip-primary mb-3">/ DT6 · SIZZLE</div>
            <h1 className="font-heading text-5xl md:text-6xl font-black leading-tight">
              America First. <span style={{ color: "var(--dt6-primary)" }}>America Only.</span>
            </h1>
            <p className="mt-4 text-lg text-[color:var(--dt6-muted)] max-w-2xl">
              Sixty seconds of the DT6 arsenal — the Source&nbsp;→&nbsp;Clean&nbsp;→&nbsp;Identify&nbsp;→&nbsp;Score&nbsp;→&nbsp;Route&nbsp;→&nbsp;Activate flywheel running as it does in production.
            </p>
          </div>
        </div>
      </Section>

      <Section className="pt-2 pb-16" testId="sizzle-player-wrap">
        <SizzlePlayer testId="sizzle-page-player" />

        <div className="mt-8 grid sm:grid-cols-2 gap-3">
          <Link
            to="/alignment/apply"
            className="dt6-btn dt6-btn-primary justify-center"
            data-testid="sizzle-apply-cta"
          >
            Apply to be armed <ArrowRight size={16} className="ml-2" />
          </Link>
          <button
            type="button"
            onClick={share}
            className="dt6-btn dt6-btn-secondary justify-center"
            data-testid="sizzle-share-cta"
            title="Copies the /api/share/sizzle URL — crawler-optimised so LinkedIn/X render an inline player."
          >
            <Share2 size={16} className="mr-2" /> Share this reel
          </button>
        </div>

        <p className="mt-4 font-mono text-[0.65rem] tracking-[0.14em] uppercase text-[color:var(--dt6-muted)]" data-testid="sizzle-share-url-hint">
          / SHARE URL · <span className="text-[color:var(--dt6-text)]">{shareUrl}</span>
        </p>
      </Section>

      <PublicFooter />
    </>
  );
}
